import { Link } from 'react-router-dom';
import { ChevronRight, Settings } from 'lucide-react';

import { AppShell } from '@/components/layout/AppShell';
import { Card, PageContainer, PageHeader } from '@/components/ui';
import {
  ComercialIcon,
  ClientesIcon,
  ComprasIcon,
  ManutencaoIcon,
  HomeIcon,
  ProjetosIcon,
  FinanceiroIcon,
  UserIcon,
  BellIcon,
  HelpIcon,
  MegafoneIcon,
  RhIcon,
} from '@/components/icons';
import { useNavegacao } from '@/lib/navegacao/use-navegacao';
import type { IconeNav, ItemNav } from '@/lib/navegacao/navegacao';
import { VERSION_FULL } from '@/version';

/**
 * /mais — a navegação COMPLETA no celular.
 *
 * Visível pela barra inferior (`MobileTabBar`), que dedica o 5º slot ao "Mais".
 *
 * 🔴 MOBILE-COMPLETO (M1, 22/10/2026): esta tela era uma lista escrita à mão
 * e não acompanhou o menu do computador — Leads, Metas, Chamados, Corretiva,
 * Comunicados e Configurações não tinham caminho nenhum no celular. ✅ Agora
 * ela desenha as MESMAS seções do menu lateral (`useNavegacao`), com as MESMAS
 * permissões, e cada área mostra as suas sub-seções abertas: qualquer tela do
 * menu fica a dois toques da barra inferior (Mais → a tela). Item novo no menu
 * do computador aparece aqui sozinho.
 *
 * **Sobre / Versão (BUG-V2-002, 2026-05-18):** última linha da página mostra
 * `VERSION_FULL`. No desktop a versão fica no rodapé do /perfil.
 */
const ICONES: Record<IconeNav, React.FC<{ className?: string }>> = {
  home: HomeIcon,
  clientes: ClientesIcon,
  comercial: ComercialIcon,
  manutencao: ManutencaoIcon,
  projetos: ProjetosIcon,
  financeiro: FinanceiroIcon,
  compras: ComprasIcon,
  comunicados: MegafoneIcon,
  rh: RhIcon,
  ajuda: HelpIcon,
  configuracoes: Settings,
};

export function MaisPage() {
  const secoes = useNavegacao();

  return (
    <AppShell>
      {/* LAYOUT-CANONICO: o `mx-auto max-w-3xl px-4 py-6 md:px-8 md:py-10`
          próprio virou a régua única. */}
      <PageContainer>
        <PageHeader
          crumb="Mais"
          title="Navegação completa"
          subtitle="Todas as áreas do sistema que você pode abrir."
        />

        {secoes.map((s) => (
          <section key={s.chave} className="mb-6" data-testid={`mais-secao-${s.chave}`}>
            {s.titulo && <SecaoTitulo>{s.titulo}</SecaoTitulo>}
            <nav className="grid grid-cols-1 gap-2" aria-label={s.titulo ?? 'Início'}>
              {s.itens.map((item) =>
                item.subItens.length > 0 ? (
                  <AreaComSecoes key={`${item.chave}-${item.to}`} item={item} />
                ) : (
                  <MenuLink
                    key={item.chave}
                    to={item.to}
                    icon={iconeDe(item.icone)}
                    label={item.label}
                  />
                ),
              )}
            </nav>
          </section>
        ))}

        <section className="mb-6" data-testid="mais-conta">
          <SecaoTitulo>Conta</SecaoTitulo>
          <nav className="grid grid-cols-1 gap-2" aria-label="Conta">
            <MenuLink to="/perfil" icon={<UserIcon className="h-5 w-5" />} label="Perfil" />
            <MenuLink
              to="/perfil/notificacoes"
              icon={<BellIcon className="h-5 w-5" />}
              label="Notificações"
              hint="O que você recebe e por onde"
            />
          </nav>
        </section>

        <section data-testid="mais-sobre">
          <SecaoTitulo>Sobre</SecaoTitulo>
          <Card variant="flat" className="px-4 py-3">
            <p className="text-sm text-ink-1 dark:text-ink-dark-1">Versão do app</p>
            <p
              className="select-all break-all font-mono text-xs text-ink-2 dark:text-ink-dark-2"
              data-testid="mais-version-full"
            >
              {VERSION_FULL}
            </p>
            <p className="mt-2 text-xs text-ink-3 dark:text-ink-dark-3">
              Atualiza sozinho a cada publicação. No computador, a mesma informação
              aparece no rodapé do seu perfil.
            </p>
          </Card>
        </section>
      </PageContainer>
    </AppShell>
  );
}

function iconeDe(icone: IconeNav) {
  const Icone = ICONES[icone];
  return <Icone className="h-5 w-5" />;
}

/** Uma área com sub-seções: o nome da área e, logo abaixo, cada seção dela. */
function AreaComSecoes({ item }: { item: ItemNav }) {
  return (
    <div
      className="rounded-xl border border-line bg-paper-surface dark:border-line-dark dark:bg-paper-dark-surface"
      data-testid={`mais-area-${item.chave}`}
    >
      <Link
        to={item.to}
        className="flex min-h-11 items-center gap-3 px-4 py-3 text-ink-1 dark:text-ink-dark-1"
      >
        <span className="shrink-0 text-ink-3 dark:text-ink-dark-3">{iconeDe(item.icone)}</span>
        <span className="min-w-0 flex-1 text-sm font-semibold">{item.label}</span>
        <ChevronRight className="h-4 w-4 shrink-0 text-ink-3 dark:text-ink-dark-3" aria-hidden />
      </Link>
      <ul className="grid grid-cols-2 gap-1.5 border-t border-line-soft px-3 pb-3 pt-2 dark:border-line-soft-dark">
        {item.subItens.map((sub) => (
          <li key={sub.to}>
            <Link
              to={sub.to}
              className="flex min-h-11 items-center rounded-lg px-3 text-sm text-ink-2 transition-colors hover:bg-paper hover:text-ink-1 dark:text-ink-dark-2 dark:hover:bg-paper-dark dark:hover:text-ink-dark-1"
            >
              {sub.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SecaoTitulo({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
      {children}
    </h2>
  );
}

function MenuLink({
  to,
  label,
  hint,
  icon,
}: {
  to: string;
  label: string;
  /** Uma linha explicando o que tem lá dentro — some quando o nome já basta. */
  hint?: string;
  // B2 Lote 4 #8: ícone OBRIGATÓRIO — previne itens sem ícone (regressão do bug).
  icon: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-xl border border-line bg-paper-surface px-4 py-3 text-ink-1 transition-colors hover:border-red dark:border-line-dark dark:bg-paper-dark-surface dark:text-ink-dark-1"
    >
      <span className="shrink-0 text-ink-3 dark:text-ink-dark-3">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{label}</span>
        {hint && (
          <span className="block text-xs text-ink-2 dark:text-ink-dark-2">{hint}</span>
        )}
      </span>
      <ChevronRight
        className="h-4 w-4 shrink-0 text-ink-3 dark:text-ink-dark-3"
        aria-hidden
      />
    </Link>
  );
}
